import { Component, computed, DestroyRef, inject, input, LOCALE_ID, OnInit, output, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { catchError, debounceTime, of, tap } from 'rxjs';
import { PharmacyService } from '../../../../services/pharmacy/pharmacy.service';
import { FilterSortingComponent } from '../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap } from 'rxjs';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { ApprovedPharmacyFilters } from '../../../../pages/admin-dashboard/admin-approved-pharmacies/approved-pharmacies.model';
import { LookupService } from '../../../../services/pharmacy/lookup.service';
import { PharmacyAutoCompleteApiItem } from '../../../../services/pharmace-auto-complete/pharmace-auto-complete.service';
import { FilterMultiSelectComponent } from '../../../../shared/ui/shared-filters/filter-multi-select-component/filter-multi-select-component';
import { FilterSelectComponent } from '../../../../shared/ui/shared-filters/filter-select-component/filter-select-component';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { PharmacyAutoComplete } from '../../invoice-list-component/shared/pharmacy-auto-complete/pharmacy-auto-complete';
import { AssignRepresentative } from '../../../admin-customer-shared-components/create-pharmacy-component/assign-representative/assign-representative';

@Component({
  selector: 'app-approved-ph-telesales-filters',
  imports: [
    HasScopeDirective,
    FilterMultiSelectComponent,
    FilterSelectComponent,
    SubmitButton,
    PharmacyAutoComplete,
    AssignRepresentative,
    FilterSortingComponent,
  ],
  templateUrl: './approved-ph-telesales-filters.html',
  styleUrl: './approved-ph-telesales-filters.scss',
})
export class ApprovedPhTelesalesFilters implements OnInit {
  private locale = inject(LOCALE_ID);
  private lookupService = inject(LookupService);
  private destroyRef = inject(DestroyRef);
  private pharmacyService = inject(PharmacyService);


  readonly SCOPES = SCOPES;

  filters = input.required<ApprovedPharmacyFilters>();

  toggleSortOrder = output();
  applyFilters = output<ApprovedPharmacyFilters>();
  resetFilters = output();

  pharmacyClear = signal(0);
  selectedPharmacyId = signal<number | null>(null);

  representativeForm = new FormGroup({ representative: new FormControl('') });
  representativeResults = signal<{ id: number; fullName: string }[]>([]);
  representativeLoading = signal(false);
  representativeError = signal(false);
  representativeClear = signal(0);
  selectedRepresentativeId = signal<number | null>(null);
  private representativeSearch$ = new Subject<string>();

  statuses = signal<LookupVTO[]>([]);
  selectedStatusIds = signal<string[]>([]);
  showStatusDropdown = signal(false);

  sortOptions = computed(() => [
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modified', labelAr: 'آخر تعديل' },
    { value: 'CREATION_DATE', labelEn: 'Creation Date', labelAr: 'تاريخ الإنشاء' },
    { value: 'PHARMACY_OWNER', labelEn: 'Pharmacy Owner', labelAr: 'مالك الصيدلية' },
  ]);

  governorates = signal<LookupVTO[]>([]);
  selectedGovernorateIds = signal<string[]>([]);
  showGovernorateDropdown = signal(false);

  cities = signal<LookupVTO[]>([]);
  selectedCityId = signal<number | null>(null);

  regions = signal<LookupVTO[]>([]);
  selectedRegionId = signal<number | null>(null);

  private governorateChange$ = new Subject<number[]>();
  private cityChange$ = new Subject<number[]>();

  isArabic() {
    return this.locale.startsWith('ar');
  }

  ngOnInit(): void {
    this.loadStatuses();

    this.representativeSearch$
      .pipe(
        tap((term) => {
          this.representativeError.set(false);
          if (!term) this.representativeResults.set([]);
        }),
        debounceTime(300),
        switchMap((term) => {
          if (!term) return of(null);
          this.representativeLoading.set(true);
          return this.pharmacyService.pharmacyRepresentativeAutoComplete(term).pipe(
            catchError(() => {
              this.representativeError.set(true);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        this.representativeLoading.set(false);
        if (res) this.representativeResults.set(res.list);
      });

    this.lookupService.getGovernorates().subscribe({
      next: (res) => this.governorates.set(res.list),
      error: () => {},
    });

    this.governorateChange$
      .pipe(
        switchMap((ids) => this.lookupService.getCities(ids)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({ next: (res) => this.cities.set(res.list), error: () => {} });

    this.cityChange$
      .pipe(
        switchMap((ids) => this.lookupService.getRegions(ids)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({ next: (res) => this.regions.set(res.list), error: () => {} });
  }

  loadStatuses() {
    this.lookupService
      .getPharmacyStatuses()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res) => this.statuses.set(res.list), error: () => {} });
  }

  statusOptions() {
    return this.statuses().map((s) => ({ id: String(s.id), titleAr: s.titleAr, titleEn: s.titleEn }));
  }

  onRepresentativeSelected(item: { id: string; fullName: string }) {
    this.selectedRepresentativeId.set(Number(item.id));
  }

  onRepresentativeSearch(term: string) {
    this.selectedRepresentativeId.set(null);
    this.representativeSearch$.next(term.trim());
  }

  onPharmacySelected(item: PharmacyAutoCompleteApiItem) {
    this.selectedPharmacyId.set(item.id);
  }

  governorateOptions() {
    return this.governorates().map((g) => ({ id: String(g.id), titleAr: g.titleAr, titleEn: g.titleEn }));
  }

  cityOptions() {
    return this.cities().map((c) => ({ id: c.id, titleAr: c.titleAr, titleEn: c.titleEn }));
  }

  regionOptions() {
    return this.regions().map((r) => ({ id: r.id, titleAr: r.titleAr, titleEn: r.titleEn }));
  }

  onGovernorateChange(ids: string[]) {
    this.selectedGovernorateIds.set(ids);
    this.selectedCityId.set(null);
    this.selectedRegionId.set(null);
    this.cities.set([]);
    this.regions.set([]);
    if (ids.length > 0) this.governorateChange$.next(ids.map(Number));
  }

  onCityChange(cityId: number | null) {
    this.selectedCityId.set(cityId ?? null);
    this.selectedRegionId.set(null);
    this.regions.set([]);
    if (cityId) this.cityChange$.next([Number(cityId)]);
  }

  onClick(event: Event) {
    event.stopPropagation();
  }

  onApply() {
    this.applyFilters.emit({
      pharmacyId: this.selectedPharmacyId() ?? undefined,
      representativeId: this.selectedRepresentativeId() ?? undefined,
      governorateIds: this.selectedGovernorateIds().length ? this.selectedGovernorateIds().map(Number) : undefined,
      cityId: this.selectedCityId() ?? undefined,
      regionId: this.selectedRegionId() ?? undefined,
      statusIds: this.selectedStatusIds().length ? this.selectedStatusIds().map(Number) : undefined,
    });
    this.showGovernorateDropdown.set(false);
  }

  onReset() {
    this.pharmacyClear.update((v) => v + 1);
    this.representativeClear.update((v) => v + 1);
    this.selectedRepresentativeId.set(null);
    this.representativeResults.set([]);
    this.selectedStatusIds.set([]);
    this.showStatusDropdown.set(false);
    this.selectedPharmacyId.set(null);
    this.selectedGovernorateIds.set([]);
    this.selectedCityId.set(null);
    this.selectedRegionId.set(null);
    this.cities.set([]);
    this.regions.set([]);
    this.showGovernorateDropdown.set(false);
    this.resetFilters.emit();
  }
}
